import { Checkbox, FlexLayout, Icon, Input, Modal } from '@deity-io/portal-ui';
import type { CreateEditModelInput } from 'contracts/interfaces';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import {
  GetMockDataDocument,
  useCreateMockDataMutation,
  useUpdateMockDataMutation
} from 'services/graphql/generated/hooks';
import type { GetMockDataQuery } from 'services/graphql/generated/operations';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface SaveEntityMockDataModalProps {
  isOpen?: boolean;
  packageVersionUuid: string;
  modelUuid?: string;
  endpointUuid?: string;
  mappingUuid?: string;
  editMockData?: GetMockDataQuery;
  setOpenState: (state: boolean) => void;
  onMockDataSaved: (uuid: string) => void;
  mockData: string;
}

export const SaveEntityMockDataModal: FC<SaveEntityMockDataModalProps> = ({
  isOpen = false,
  setOpenState,
  onMockDataSaved,
  modelUuid,
  endpointUuid,
  mappingUuid,
  packageVersionUuid,
  editMockData,
  mockData
}) => {
  const [saveCopy, setSaveCopy] = useState<boolean>(false);
  const [newUuid, setNewUuid] = useState<string>(v4());
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetMockDataDocument,
        variables: {
          uuid: editMockData?.getMockData.uuid,
          packageVersionUuid
        }
      }
    ]
  };
  const [create, { data: createData, loading: createLoading }] = useCreateMockDataMutation();
  const [update, { data: updateData, loading: updateLoading }] = useUpdateMockDataMutation(refetchQueries);

  const CreateMockDataInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(100, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(255, 'Too long')
  });

  useEffect(() => {
    if (createData) {
      onMockDataSaved(newUuid);
      setNewUuid(v4());
      setOpenState(false);
    }
  }, [createData]);

  useEffect(() => {
    if (updateData) {
      onMockDataSaved(editMockData?.getMockData.uuid!);
      setOpenState(false);
    }
  }, [updateData]);

  const createOrUpdate = (input: CreateEditModelInput) => {
    if (!input.description) {
      input.description = '';
    }

    return editMockData?.getMockData && !saveCopy ? updateMockData(input) : createMockData(input);
  };

  const createMockData = (input: CreateEditModelInput) => {
    create({
      variables: {
        uuid: newUuid,
        packageVersionUuid,
        name: input.name,
        description: input.description,
        source: {},
        endpointUuid,
        modelUuid,
        mappingUuid,
        data: mockData
      }
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`Failed creating mockData. Message ${err}`);
    });
  };

  const formik = useFormik({
    initialValues: {
      name: editMockData?.getMockData.name ?? '',
      description: editMockData?.getMockData.description ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreateMockDataInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editMockData?.getMockData) {
      const { name, description } = editMockData.getMockData;
      formik.setValues({ name: name!, description: description! });
    }
  }, [editMockData?.getMockData]);

  const updateMockData = (input: CreateEditModelInput) => {
    update({
      variables: {
        packageVersionUuid,
        uuid: editMockData?.getMockData.uuid!,
        name: input.name,
        description: input.description,
        source: {},
        data: mockData
      }
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`Failed updating mockData. Message ${err}`);
    });
  };

  const confirmText = () => {
    if (!editMockData?.getMockData || saveCopy) {
      return `${createLoading ? 'Creating' : 'Create'} data`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} data`;
  };

  // if (isEdit && !editEndpoint) return null!;
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label="Save test data"
      title="Please fill in the details"
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <FlexLayout flexDirection="column">
          <Input
            key="name"
            autoFocus
            required
            label="Name"
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onBlur={() => formik.setFieldTouched('name', true)}
            onChange={e => formik.setFieldValue('name', e.target.value)}
            placeholder="Name"
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            sx={{ marginY: 2 }}
          />

          <Input
            key="description"
            label="Description"
            type="text"
            name="description"
            id="description"
            value={formik.values.description}
            onBlur={() => formik.setFieldTouched('description', true)}
            onChange={e => formik.setFieldValue('description', e.target.value)}
            placeholder="Description"
            error={!!formik.errors.description}
            helperText={formik.errors.description}
            sx={{ marginY: 2 }}
          />
          {editMockData?.getMockData && (
            <Checkbox
              checked={saveCopy}
              onChange={val => {
                setSaveCopy(val.target.checked);
              }}
              label="Save as copy"
            />
          )}
        </FlexLayout>
      </form>
    </Modal>
  );
};
